/*
  Yarnly Crochet — predictive search.
  Element: yarnly-predictive-search (snippets/header-search.liquid).

  Progressive enhancement only: the wrapped <form> has a real action/method
  and submits to the search page on its own. When settings.predictive_search
  is off the element carries data-disabled and nothing below runs.
*/
(function () {
  'use strict';
  const S = window.Yarnly;
  if (!S) return;

  const MIN_TERM_LENGTH = 2;
  const DEBOUNCE_MS = 300;
  const RESOURCE_TYPES = 'product,collection,page,article,query';

  if (!customElements.get('yarnly-predictive-search')) {
    class YarnlyPredictiveSearch extends HTMLElement {
      constructor() {
        super();
        this.onInput = this.onInput.bind(this);
        this.search = this.search.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onReset = this.onReset.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.controller = null;
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('[data-predictive-input]');
        this.results = this.querySelector('[data-predictive-results]');
        this.status = this.querySelector('[data-predictive-status]');
        this.resetButton = this.querySelector('[data-predictive-reset]');
        this.quick = this.querySelector('[data-predictive-quick]');

        /* Predictive search turned off in theme settings: leave the native
           form (and the quick links) untouched. */
        if (this.hasAttribute('data-disabled')) return;
        if (!this.form || !this.input || !this.results) return;

        this.enhanced = true;
        this.sectionId = this.dataset.sectionId || 'predictive-search';
        this.limit = this.dataset.limit || 6;
        this.searchDebounced = S.utils.debounce(this.search, DEBOUNCE_MS);

        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('keydown', this.onKeydown);
        if (this.resetButton) this.resetButton.addEventListener('click', this.onReset);
        this.addEventListener('focusout', this.onFocusOut);
        document.addEventListener('click', this.onDocumentClick);

        this.toggleReset();
        this.toggleQuick();
      }

      disconnectedCallback() {
        this.abort();
        if (this.input) {
          this.input.removeEventListener('input', this.onInput);
          this.input.removeEventListener('keydown', this.onKeydown);
        }
        if (this.resetButton) this.resetButton.removeEventListener('click', this.onReset);
        this.removeEventListener('focusout', this.onFocusOut);
        document.removeEventListener('click', this.onDocumentClick);
      }

      /* Requests ------------------------------------------------------- */

      abort() {
        if (this.controller) this.controller.abort();
        this.controller = null;
      }

      getTerm() {
        return this.input ? this.input.value.trim() : '';
      }

      onInput() {
        this.toggleReset();
        this.toggleQuick();
        this.searchDebounced();
      }

      search() {
        const term = this.getTerm();
        if (term.length < MIN_TERM_LENGTH) {
          this.abort();
          this.close();
          return;
        }

        this.abort();
        const controller = new AbortController();
        this.controller = controller;
        this.results.setAttribute('aria-busy', 'true');

        const url =
          `${S.routes.predictiveSearch}?q=${encodeURIComponent(term)}` +
          `&section_id=${this.sectionId}` +
          `&resources[type]=${RESOURCE_TYPES}` +
          `&resources[limit]=${this.limit}` +
          `&resources[limit_scope]=each`;

        fetch(url, { headers: { Accept: 'text/html' }, signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((text) => {
            if (this.controller !== controller) return;
            this.controller = null;
            this.render(text);
          })
          .catch((error) => {
            if (error && error.name === 'AbortError') return;
            if (this.controller !== controller) return;
            this.controller = null;
            this.results.setAttribute('aria-busy', 'false');
            if (this.status) this.status.textContent = S.strings.searchError || '';
            this.close();
          });
      }

      render(text) {
        const doc = S.utils.parseHTML(text);
        const section = doc.getElementById('shopify-section-' + this.sectionId) || doc.querySelector('.predictive-search');
        if (!section) {
          this.close();
          return;
        }

        this.results.innerHTML = section.innerHTML;
        this.results.setAttribute('aria-busy', 'false');
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        this.input.removeAttribute('aria-activedescendant');
        this.toggleQuick();
        this.announce();
      }

      /* The rendered section carries its own localized summary and count, so
         no extra locale key is needed; a plain number is the fallback. */
      announce() {
        if (!this.status) return;
        const panel = this.results.querySelector('.predictive-search');
        const options = this.getOptions();
        let total = options.length;
        if (panel && panel.hasAttribute('data-predictive-count')) {
          const parsed = parseInt(panel.getAttribute('data-predictive-count'), 10);
          if (!isNaN(parsed)) total = parsed;
        }
        if (total < 1) {
          this.status.textContent = S.strings.noResults || '';
          return;
        }
        const summary = panel ? panel.getAttribute('data-predictive-summary') : '';
        this.status.textContent = summary || String(total);
      }

      /* Open / closed state -------------------------------------------- */

      close() {
        if (!this.results) return;
        this.deselect();
        this.results.hidden = true;
        this.results.setAttribute('aria-busy', 'false');
        if (this.input) {
          this.input.setAttribute('aria-expanded', 'false');
          this.input.removeAttribute('aria-activedescendant');
        }
        this.toggleQuick();
      }

      /* Called by yarnly-search-modal when the drawer closes. */
      clear() {
        this.abort();
        if (this.input) this.input.value = '';
        if (this.results) this.results.innerHTML = '';
        if (this.status) this.status.textContent = '';
        this.close();
        this.toggleReset();
      }

      isOpen() {
        return Boolean(this.results) && !this.results.hidden;
      }

      toggleReset() {
        if (!this.resetButton) return;
        this.resetButton.hidden = this.getTerm().length === 0;
      }

      /* Quick links sit under the field until a search is under way: they
         go as soon as the term is long enough to query, or results show. */
      toggleQuick() {
        if (!this.quick) return;
        this.quick.hidden = this.isOpen() || this.getTerm().length >= MIN_TERM_LENGTH;
      }

      onReset(event) {
        event.preventDefault();
        this.abort();
        if (this.input) this.input.value = '';
        if (this.status) this.status.textContent = '';
        this.close();
        this.toggleReset();
        if (this.input) this.input.focus();
      }

      /* Keyboard ------------------------------------------------------- */

      getOptions() {
        if (!this.results) return [];
        return Array.from(this.results.querySelectorAll('[role="option"]'));
      }

      getLink(option) {
        if (!option) return null;
        if (option.tagName === 'A' && option.hasAttribute('href')) return option;
        return option.querySelector('a[href]');
      }

      getSelectedLink() {
        const options = this.getOptions();
        for (let i = 0; i < options.length; i += 1) {
          if (options[i].getAttribute('aria-selected') === 'true') return this.getLink(options[i]);
        }
        return null;
      }

      deselect() {
        this.getOptions().forEach((option) => {
          option.setAttribute('aria-selected', 'false');
          const link = this.getLink(option);
          if (link) link.removeAttribute('data-selected');
        });
        if (this.input) this.input.removeAttribute('aria-activedescendant');
      }

      /* The list is replaced on every render, so options are queried fresh. */
      move(delta) {
        const options = this.getOptions();
        if (!options.length) return;

        let current = -1;
        options.forEach((option, i) => {
          if (option.getAttribute('aria-selected') === 'true') current = i;
        });
        if (current < 0) current = delta > 0 ? -1 : 0;

        let next = current + delta;
        if (next < 0) next = options.length - 1;
        if (next > options.length - 1) next = 0;

        options.forEach((option, i) => {
          const active = i === next;
          option.setAttribute('aria-selected', active ? 'true' : 'false');
          const link = this.getLink(option);
          if (link) {
            if (active) link.setAttribute('data-selected', '');
            else link.removeAttribute('data-selected');
          }
          if (!active) return;
          if (!option.id) option.id = 'PredictiveOption-' + (i + 1);
          this.input.setAttribute('aria-activedescendant', option.id);
          if (typeof option.scrollIntoView === 'function') option.scrollIntoView({ block: 'nearest' });
        });
      }

      onKeydown(event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          if (!this.isOpen()) return;
          event.preventDefault();
          this.move(event.key === 'ArrowDown' ? 1 : -1);
          return;
        }

        if (event.key === 'Enter') {
          const link = this.isOpen() ? this.getSelectedLink() : null;
          /* No highlight: let the form submit to the search page. */
          if (!link) return;
          event.preventDefault();
          link.click();
          return;
        }

        if (event.key === 'Escape') {
          if (this.isOpen()) {
            event.preventDefault();
            event.stopPropagation();
            this.abort();
            this.close();
            return;
          }
          if (this.getTerm().length) {
            event.preventDefault();
            event.stopPropagation();
            this.input.value = '';
            if (this.status) this.status.textContent = '';
            this.toggleReset();
            this.toggleQuick();
          }
          /* Empty input: the drawer handles Escape and closes. */
        }
      }

      /* Dismissal ------------------------------------------------------ */

      onFocusOut(event) {
        const next = event.relatedTarget;
        /* relatedTarget is null when focus goes to the browser chrome or to a
           non-focusable area; those cases are covered by onDocumentClick, and
           ignoring them here keeps a mousedown on a result from cancelling the
           click that follows it. */
        if (!next) return;
        if (this.contains(next)) return;
        this.close();
      }

      onDocumentClick(event) {
        if (!this.isOpen()) return;
        if (this.contains(event.target)) return;
        this.close();
      }
    }

    customElements.define('yarnly-predictive-search', YarnlyPredictiveSearch);
  }
})();
